import { Controller } from "@hotwired/stimulus"

// Customer questionnaire WIZARD: documents shown as a left stepper, one document
// visible at a time, global progress bar, search + hide-answered filters, and
// Back/Next navigation. Reuses the server progressive-disclosure recompute (the
// #runtime action returns the whole wizard body, which we swap into `region`).
const CIRCLE = {
  complete: ["border-ps-dark-blue", "bg-ps-dark-blue", "text-white"],
  active:   ["border-ps-dark-blue", "bg-white", "text-ps-dark-blue"],
  pending:  ["border-gray-300", "bg-white", "text-gray-400"]
}
const LABEL = {
  complete: ["text-ps-dark-blue"],
  active:   ["text-ps-dark-blue"],
  pending:  ["text-gray-400"]
}
const ALL_CIRCLE = [...new Set([...CIRCLE.complete, ...CIRCLE.active, ...CIRCLE.pending])]
const ALL_LABEL = [...new Set([...LABEL.complete, ...LABEL.active, ...LABEL.pending])]

// Yes/No pill styling, applied optimistically while the save request is in flight.
const BOOL = {
  on_true:  ["bg-emerald-600", "text-white", "shadow-sm"],
  on_false: ["bg-red-600", "text-white", "shadow-sm"],
  off:      ["text-gray-500", "hover:text-gray-700"]
}
const BOOL_ALL = [...new Set([...BOOL.on_true, ...BOOL.on_false, ...BOOL.off])]

export default class extends Controller {
  static values = { url: String }
  static targets = ["form", "region", "search", "hideAnsweredLabel", "noResults", "prev", "next", "nextLabel"]

  connect() {
    this.abortController = null
    this.refreshTimer = null
    this.hideAnswered = false
    this.searchTerm = ""
    this.onFormChange = this.onFormChange.bind(this)
    if (this.hasFormTarget) this.formTarget.addEventListener("change", this.onFormChange)

    this.activeId = this.readActiveId()
    this.applyActive()
    this.applyFilters()

    // An "Incomplete" badge links here with ?open=1: land on the missing question itself (#16).
    if (new URLSearchParams(window.location.search).get("open") === "1") {
      window.setTimeout(() => this.jumpToFirstUnanswered(), 300)
    }
  }

  disconnect() {
    if (this.hasFormTarget) this.formTarget.removeEventListener("change", this.onFormChange)
    if (this.abortController) this.abortController.abort()
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
  }

  // ── Navigation ──
  readActiveId() {
    const root = this.regionTarget.querySelector("[data-active-document-id]")
    return root ? Number(root.dataset.activeDocumentId) : (this.stepIds()[0] || 0)
  }

  stepIds() {
    return Array.from(this.regionTarget.querySelectorAll("[data-wiz-step]")).map((b) => Number(b.dataset.docId))
  }

  goto(event) {
    event.preventDefault()
    const id = Number(event.currentTarget.dataset.docId)
    if (id) this.setActive(id)
  }

  next(event) {
    event?.preventDefault()
    const ids = this.stepIds()
    const i = ids.indexOf(this.activeId)
    const isLast = i === ids.length - 1

    // #16: "Weiter" moved on without saying anything was left open. The first
    // click takes the customer to the first open question — in this document, or
    // anywhere before finishing — and turns the button into "Trotzdem weiter";
    // the second click moves on, so nobody is forced to answer before looking ahead.
    const open = isLast ? this.firstUnansweredItem() : this.openItemIn(this.activeId)
    const warnKey = isLast ? "finish" : this.activeId
    if (open && this.warnedFor !== warnKey) {
      this.warnedFor = warnKey
      this.jumpToFirstUnanswered()
      if (this.hasNextLabelTarget && this.nextLabelTarget.dataset.skipText) {
        this.nextLabelTarget.textContent = this.nextLabelTarget.dataset.skipText
      }
      return
    }

    if (i > -1 && i < ids.length - 1) {
      this.setActive(ids[i + 1])
    } else if (this.hasFormTarget && !this.finishing) {
      // Last step → persist all answers (the #update action saves + redirects). The save takes seconds and the
      // page said nothing meanwhile, so "Finish" read as doing nothing and a second click saved twice (QA).
      this.finishing = true
      if (this.hasNextTarget) this.nextTarget.disabled = true
      if (this.hasNextLabelTarget && this.nextLabelTarget.dataset.savingText) {
        this.nextLabelTarget.textContent = this.nextLabelTarget.dataset.savingText
      }
      this.nextTarget?.querySelector("i")?.classList.replace("fa-arrow-right", "fa-spinner")
      this.nextTarget?.querySelector("i")?.classList.add("fa-spin")
      this.formTarget.addEventListener("turbo:submit-end", (event) => {
        if (event.detail?.success) return
        this.finishing = false
        if (this.hasNextTarget) this.nextTarget.disabled = false
        this.nextTarget?.querySelector("i")?.classList.remove("fa-spin")
        this.nextTarget?.querySelector("i")?.classList.replace("fa-spinner", "fa-arrow-right")
        this.applyActive()
      }, { once: true })
      this.formTarget.requestSubmit()
    }
  }

  openItemIn(docId) {
    return this.regionTarget.querySelector(
      `[data-doc-panel="${docId}"] .js-questionnaire-item[data-answered="false"]`
    )
  }

  prev(event) {
    event?.preventDefault()
    const ids = this.stepIds()
    const i = ids.indexOf(this.activeId)
    if (i > 0) this.setActive(ids[i - 1])
  }

  setActive(id) {
    this.activeId = id
    this.applyActive()
    this.applyFilters()
    // scrollIntoView tucks the wizard under the sticky top bar and reads as a jump.
    window.scrollTo({ top: 0, behavior: "smooth" })
    // Recompute on navigation so text answers (saved with swap:false) mark their step done.
    this.refreshRuntime({ swap: true })
  }

  applyActive() {
    const ids = this.stepIds()
    if (!ids.includes(this.activeId)) this.activeId = ids[0] || 0

    // Panels: only the active document is shown.
    this.regionTarget.querySelectorAll("[data-doc-panel]").forEach((panel) => {
      panel.classList.toggle("hidden", Number(panel.dataset.docPanel) !== this.activeId)
    })

    // Stepper: circle + label + check/number per (active / complete / pending).
    this.regionTarget.querySelectorAll("[data-wiz-step]").forEach((step) => {
      const isActive = Number(step.dataset.docId) === this.activeId
      const isComplete = step.dataset.complete === "true"
      const variant = isComplete ? "complete" : (isActive ? "active" : "pending")

      const circle = step.querySelector("[data-wiz-circle]")
      const label = step.querySelector("[data-wiz-label]")
      const check = step.querySelector("[data-wiz-check]")
      const number = step.querySelector("[data-wiz-number]")

      if (circle) { circle.classList.remove(...ALL_CIRCLE); circle.classList.add(...CIRCLE[variant]) }
      if (label) { label.classList.remove(...ALL_LABEL); label.classList.add(...LABEL[variant]) }
      if (check) check.classList.toggle("hidden", !isComplete)
      if (number) number.classList.toggle("hidden", isComplete)
    })

    // Back disabled on the first step. Next stays enabled — on the last step it
    // becomes "Finish" and submits the form instead of advancing.
    const i = ids.indexOf(this.activeId)
    const isLast = i >= ids.length - 1
    if (this.hasPrevTarget) {
      // On the first step there's nowhere to go back to (the "leave" link at the
      // top handles exiting), so hide Back rather than show a dead, greyed-out
      // button. `invisible` keeps its box so Next stays right-aligned.
      const atStart = i <= 0
      this.prevTarget.disabled = atStart
      this.prevTarget.classList.toggle("invisible", atStart)
    }
    if (this.hasNextLabelTarget) {
      this.nextLabelTarget.textContent = isLast
        ? this.nextLabelTarget.dataset.finishText
        : this.nextLabelTarget.dataset.nextText
    }
  }

  // ── Filters (search + hide-answered), scoped to the active document ──
  filter(event) {
    this.searchTerm = (event.target.value || "").trim().toLowerCase()
    this.applyFilters()
  }

  toggleHideAnswered(event) {
    event.preventDefault()
    this.hideAnswered = !this.hideAnswered
    this.hideAnsweredLabelTargets.forEach((l) => {
      l.textContent = this.hideAnswered ? l.dataset.showText : l.dataset.hideText
    })
    this.applyFilters()
  }

  // "Weiter" moved on without saying anything was missing, so a document could be
  // left half-answered without the customer noticing (#16). This takes them to the
  // first unanswered question instead — switching document if the active one is
  // complete, because the gap is often on a step they already walked past.
  jumpToFirstUnanswered(event) {
    if (event) event.preventDefault()

    const target = this.firstUnansweredItem()
    if (!target) return

    const panelId = Number(target.closest("[data-doc-panel]")?.dataset.docPanel)
    if (!Number.isNaN(panelId) && panelId !== this.activeId) {
      this.activeId = panelId
      this.applyActive()
    }

    // Un-hide it first: it may be filtered out by the search box or hide-answered.
    this.searchTerm = ""
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.applyFilters()

    target.scrollIntoView({ behavior: "smooth", block: "center" })
    target.classList.add("ring-2", "ring-brand-300", "rounded-lg")
    window.setTimeout(() => target.classList.remove("ring-2", "ring-brand-300", "rounded-lg"), 2000)
    this.focusFirstVisibleControl(target)
  }

  // Radio and checkbox inputs are visually hidden behind styled labels here, and
  // focusing a hidden element does nothing — it just leaves focus on the button
  // that was clicked. The scroll and the ring carry the message on their own, so
  // focus moves only when there is something the customer can actually type in.
  focusFirstVisibleControl(item) {
    const control = Array.from(item.querySelectorAll("input, select, textarea")).find((el) => {
      if (el.disabled || el.type === "hidden") return false
      return el.offsetParent !== null
    })
    control?.focus({ preventScroll: true })
  }

  // The active document first — jumping backwards when the gap is right here
  // would be disorienting.
  firstUnansweredItem() {
    const here = this.regionTarget.querySelector(
      `[data-doc-panel="${this.activeId}"] .js-questionnaire-item[data-answered="false"]`
    )
    return here || this.regionTarget.querySelector('.js-questionnaire-item[data-answered="false"]')
  }

  applyFilters() {
    const panel = this.regionTarget.querySelector(`[data-doc-panel="${this.activeId}"]`)
    if (!panel) return

    let visible = 0
    panel.querySelectorAll(".js-questionnaire-item").forEach((item) => {
      const answered = item.dataset.answered === "true"
      const text = item.dataset.searchText || ""
      const hidden = (this.hideAnswered && answered) || (this.searchTerm && !text.includes(this.searchTerm))
      item.classList.toggle("hidden", hidden)
      if (!hidden) visible++
    })

    // Only a search or "hide answered" can leave nothing to show; a document with no questions of its own says so
    // itself, and "No questions match your search" there sat under it with no search typed (QA).
    const filtering = Boolean(this.searchTerm) || Boolean(this.hideAnswered)
    const hasItems = panel.querySelectorAll(".js-questionnaire-item").length > 0
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("hidden", !(filtering && hasItems && visible === 0))
      const searching = Boolean(this.searchTerm)
      this.noResultsTarget.querySelector('[data-no-results="search"]')?.classList.toggle("hidden", !searching)
      this.noResultsTarget.querySelector('[data-no-results="answered"]')?.classList.toggle("hidden", searching)
    }
  }

  // Every change persists via #runtime; selections also swap the region, free text saves without a swap.
  onFormChange(event) {
    const target = event.target
    if (!(target.getAttribute("name") || "").includes("[answer]")) return

    const item = target.closest(".js-questionnaire-item")
    const sourceType = item?.querySelector('input[name*="[source_type]"]')
    const isVariable = sourceType && sourceType.value === "variable"
    const tag = target.tagName.toLowerCase()
    const type = (target.getAttribute("type") || "").toLowerCase()
    const isSelection = !isVariable && (tag === "select" || type === "radio" || type === "checkbox")

    if (isSelection) {
      if (item) {
        this.applyOptimisticBool(item)
        this.showSpinner(item)
      }
      this.refreshRuntime({ swap: true })
    } else {
      // Persist text/variable answers without swapping the region (keeps focus).
      this.refreshRuntime({ swap: false })
    }
  }

  // Takes the status parts from a fresh render without touching any input.
  syncStatus(html) {
    const fresh = document.createElement("div")
    fresh.innerHTML = html

    ;["[data-wiz-stepper]", "[data-wiz-progress]"].forEach((selector) => {
      const current = this.regionTarget.querySelector(selector)
      const next = fresh.querySelector(selector)
      if (current && next) current.replaceWith(next)
    })

    const freshItems = new Map()
    fresh.querySelectorAll(".js-questionnaire-item").forEach((item) => freshItems.set(this.itemKey(item), item))
    this.regionTarget.querySelectorAll(".js-questionnaire-item").forEach((item) => {
      const next = freshItems.get(this.itemKey(item))
      if (!next) return

      item.dataset.answered = next.dataset.answered
      item.dataset.questionnaireAnswered = next.dataset.questionnaireAnswered
      item.querySelector("[data-questionnaire-open-badge]")?.remove()
      const badge = next.querySelector("[data-questionnaire-open-badge]")
      if (badge) item.querySelector("h3")?.appendChild(badge)
      this.syncFormatError(item, next)
    })

    this.applyActive()
  }

  syncFormatError(item, next) {
    item.querySelector("[data-questionnaire-format-error]")?.remove()
    const error = next.querySelector("[data-questionnaire-format-error]")
    if (error) item.appendChild(error)
    const input = item.querySelector('input.ps-input[name*="[answer]"], textarea[name*="[answer]"]')
    if (!input) return
    input.classList.toggle("border-red-500", !!error)
    if (error) input.setAttribute("aria-invalid", "true")
    else input.removeAttribute("aria-invalid")
  }

  itemKey(item) {
    const type = item.querySelector('input[name*="[source_type]"]')?.value
    const id = item.querySelector('input[name*="[source_id]"]')?.value
    return `${type}:${id}`
  }

  applyOptimisticBool(item) {
    item.querySelectorAll("[data-bool-option]").forEach((opt) => {
      const radio = opt.querySelector('input[type="radio"]')
      const on = !!radio?.checked
      const isTrue = opt.dataset.boolValue === "true"
      opt.classList.remove(...BOOL_ALL)
      opt.classList.add(...(on ? (isTrue ? BOOL.on_true : BOOL.on_false) : BOOL.off))
      const check = opt.querySelector("[data-bool-check]")
      if (check) check.classList.toggle("hidden", !on)
    })
  }

  showSpinner(item) {
    item.querySelector("[data-bool-spinner]")?.classList.remove("hidden")
  }

  hideSpinners() {
    this.regionTarget.querySelectorAll("[data-bool-spinner]").forEach((s) => s.classList.add("hidden"))
  }

  serializeDraftRows() {
    if (!this.hasFormTarget) return []
    const rows = []
    this.formTarget.querySelectorAll(".js-questionnaire-item").forEach((item) => {
      const st = item.querySelector('input[name*="[source_type]"]')
      const si = item.querySelector('input[name*="[source_id]"]')
      if (!st || !si) return

      const checks = item.querySelectorAll('input[type="checkbox"][name*="[answer]"]')
      const radios = item.querySelectorAll('input[type="radio"][name*="[answer]"]')
      // A checkbox field with no options on screen (a migrated free-text list)
      // offers nothing to change; sending its empty placeholder made the saved
      // answer read as missing and held the progress back until a reload.
      const placeholderOnly = checks.length === 0 && radios.length === 0 &&
        !item.querySelector('select[name*="[answer]"], textarea[name*="[answer]"], input[name*="[answer]"]:not([type="hidden"])')
      if (placeholderOnly) return
      let answer = null
      if (checks.length > 0) {
        answer = Array.from(checks).filter((c) => c.checked).map((c) => c.value)
      } else if (radios.length > 0) {
        const r = Array.from(radios).find((x) => x.checked)
        answer = r ? r.value : null
      } else {
        const input = item.querySelector('select[name*="[answer]"], textarea[name*="[answer]"], input[name*="[answer]"]')
        answer = input ? input.value : null
      }
      rows.push({ source_type: st.value, source_id: si.value, answer, explanation: "" })
    })
    return rows
  }

  async refreshRuntime({ swap = true } = {}) {
    if (!this.urlValue || !this.hasRegionTarget) return
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin",
        signal: this.abortController.signal,
        body: JSON.stringify({ document_id: this.activeId, draft_answers_rows: this.serializeDraftRows() })
      })
      if (!response.ok) { this.hideSpinners(); return }
      const payload = await response.json()
      // Text/variable persistence: saved, but no region swap (keeps the caret).
      // The progress, the step ticks and the open badges still follow (QA #27).
      if (!swap) {
        if (typeof payload.items_html === "string") this.syncStatus(payload.items_html)
        return
      }
      if (typeof payload.items_html !== "string") { this.hideSpinners(); return }

      this.regionTarget.innerHTML = payload.items_html
      this.applyActive()
      this.applyFilters()
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Questionnaire wizard refresh failed", error)
        this.hideSpinners()
      }
    }
  }
}
